import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context/authContext";
import createAxiosInstance from "../../../../utils/axiosConfig";
import { add_consultancy, add_feedback } from "../../../../utils/endpoints";
import { validate } from "../../../../utils/formValidate";
import Alert from "../../alerts/alert";
import CustomDatePicker from "./CustomDatePicker";

interface formDataType {
	first_name: string;
	last_name: string;
	contact_phone: string;
	email: string;
	comments: string;
	date_time?: string;
}

const ContactForm = ({ type, onSuccess }: { type: string; onSuccess?: () => void }) => {
	const { logout } = useAuth();
	const navigate = useNavigate();
	const userData = JSON.parse(localStorage.getItem("userData") ?? "{}");
	const [formData, setFormData] = useState<formDataType>({
		first_name: userData.first_name || "",
		last_name: userData.last_name || "",
		contact_phone: "",
		email: userData.email || "",
		comments: userData.comments || "",
		date_time: "",
	});
	const [errors, setErrors] = useState<any>({});
	const [responseError, setResponseError] = useState<any>("");
	const [alertVisible, setAlertVisible] = useState(false);
	const [loading, setLoading] = useState(false);

	const handleChange = (
		e: React.ChangeEvent<
			HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
		>
	) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
		validate({ [name]: value }, setErrors);
	};

	const handleDateTimeChange = (value: string) => {
		setFormData({ ...formData, date_time: value });
		validate({ date_time: value }, setErrors);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const validationData = type === "consultancy" 
			? formData 
			: {
				first_name: formData.first_name,
				last_name: formData.last_name,
				email: formData.email,
				contact_phone: formData.contact_phone,
				comments: formData.comments
			};

		if (!validate(validationData, setErrors)) return;
		setLoading(true);
		const payload: formDataType = {
			first_name: formData.first_name,
			last_name: formData.last_name,
			email: formData.email,
			contact_phone: formData.contact_phone,
			comments: formData.comments,
		};

		if (type === "consultancy") {
			payload.date_time = formData.date_time;
		}

		try {
			const axiosInstance = createAxiosInstance();

			const response = await axiosInstance.post(
				type == "consultancy" ? add_consultancy : add_feedback,
				payload
			);
			if (response.status == 401) {
				logout();
				navigate("/");
				return;
			}

			if (response.data?.user?.error) {
				setResponseError(response?.data?.user?.error);
				setAlertVisible(true);
				setLoading(false);
			} else {
				setAlertVisible(true);
				setFormData({
					first_name: userData.first_name || "",
					last_name: userData.last_name || "",
					contact_phone: "",
					email: userData.email || "",
					comments: "",
					date_time: "",
				});
				// Call onSuccess callback if provided (e.g., to close modal)
				if (onSuccess) {
					setTimeout(() => {
						setLoading(false);
						onSuccess();
					}, 2000); // Wait 2 seconds to show success message
				}
			}
		} catch (err) {
			setResponseError("Some errors occured. Please try again later.");
			setAlertVisible(true);
			setLoading(false);
			console.log(err);
		} finally {
			setLoading(false);
		}
	};

	return (
		<form action="" onSubmit={handleSubmit}>
			<div className="row mb-3">
				<div className="col">
					<div className="mb-3">
						<label htmlFor="firstname" className="form-label" style={{
							fontSize: '0.875rem',
						}}>First Name</label>
						<input
							type="text"
							className={`form-control bg-white  ${
								errors?.first_name ? "is-invalid" : ""
							}`}
							id="firstname"
							placeholder=""
							name="first_name"
							onChange={handleChange}
							value={formData.first_name}
						/>
						{errors?.first_name && (
							<div className="text-danger small">{errors?.first_name}</div>
						)}
					</div>
				</div>
				<div className="col">
					<div className="mb-3">
						<label htmlFor="lastname" className="form-label" style={{
							fontSize: '0.875rem',
						}}>Last Name</label>
						<input
							type="text"
							className={`form-control bg-white ${
								errors?.last_name ? "is-invalid" : ""
							}`}
							id="lastname"
							placeholder=""
							name="last_name"
							onChange={handleChange}
							value={formData.last_name}
							spellCheck={true}
						/>
						{errors?.last_name && (
							<div className="text-danger small">{errors?.last_name}</div>
						)}
					</div>
				</div>
			</div>
			<div className="row mb-3">
				<div className="col">
					<div className="mb-3">
						<label htmlFor="contactphone" className="form-label" style={{
							fontSize: '0.875rem',
						}}>Contact Phone</label>
						<input
							type="tel"
							className={`form-control bg-white ${
								errors.contact_phone ? "is-invalid" : ""
							}`}
							id="contactphone"
							placeholder=""
							name="contact_phone"
							onChange={handleChange}
							value={formData.contact_phone}
						/>
						{errors.contact_phone && (
							<div className="text-danger small">{errors.contact_phone}</div>
						)}
					</div>
				</div>
				<div className="col">
					<div className="mb-3">
						<label 
							htmlFor="email" 
							className="form-label" 
							style={{
								fontSize: '0.875rem',
							}}
						> 
							Email
						</label>
						<input
							type="email"
							className={`form-control bg-white ${errors.email ? "is-invalid" : ""}`}
							id="email"
							placeholder=""
							name="email"
							onChange={handleChange}
							value={formData.email}
						/>
						{errors.email && (
							<div className="text-danger small">{errors.email}</div>
						)}
					</div>
				</div>
			</div>

			{type === "consultancy" && (
				<div className="row mb-3">
					<div className="col">
											<div className="mb-3" style={{
						backgroundColor: '#A7CCFF',
						padding: '1.2rem 0.8rem',
						borderRadius: '1.25rem',
					}}>
						<label htmlFor="date_time" style={{
							fontSize: '0.875rem',
						}}> Schedule Date / Time</label>
						<CustomDatePicker
							value={formData.date_time || ""}
							onChange={handleDateTimeChange}
							hasError={!!errors.date_time}
						/>
						{errors.date_time && (
							<div className="text-danger small">{errors.date_time}</div>
						)}
					</div>
					</div>
				</div>
			)}

			<div className="mb-3">
				<label htmlFor="floatingTextarea" className="form-label" style={{
					fontSize: '0.875rem',
				}}>{type === "consultancy" ? "What would you like to discuss?" : "Comments"}</label>
				<textarea
					className={`form-control bg-white ${errors.comments ? "is-invalid" : ""}`}
					style={{ height: "100px" }}
					placeholder=""
					id="floatingTextarea"
					name="comments"
					onChange={handleChange}
					value={formData.comments}
					spellCheck={true}
				></textarea>
				{errors.comments && (
					<div className="text-danger small">{errors.comments}</div>
				)}
			</div>
			<div className="mb-3 text-end">
				<button type="submit" className="btn btn-rounded-blue w-40 mt-3 mb-3" style={{
					fontWeight: 'bold',
					transition: 'all 0.3s ease',
				}}
				onMouseEnter={(e) => {
					const target = e.target as HTMLButtonElement;
					target.style.backgroundColor = '#5376F0 !important';
					target.style.transform = 'translateY(-2px)';
				}}
				onMouseLeave={(e) => {
					const target = e.target as HTMLButtonElement;
					target.style.backgroundColor = '#26397E';
					target.style.transform = 'translateY(0)';
				}}
				>
					{loading ? <div
						className="d-flex justify-content-center align-items-center"
						style={{ height: "20px", color: 'white' }}
					>
						<div className="spinner-border text-white" role="status">
							<span className="visually-hidden">Loading...</span>
						</div>
					</div> : type == "feedback"
						? "SUBMIT FEEDBACK"
						: type == "consultancy"
						? "SUBMIT REQUEST"
						: "SUBMIT FORM"}
					{/* {type == "feedback"
						? "SUBMIT FEEDBACK"
						: type == "consultancy"
						? "SUBMIT REQUEST"
						: "SUBMIT FORM"} */}
				</button>
			</div>
			<Alert
				message={`${
					responseError.length > 1
						? responseError
						: type === "consultancy"
						? "Thank you for contacting us. We’ll reach out to finalize your request"
						: "Feedback form was submitted successfully."
				}`}
				type={`${responseError.length > 1 ? "danger" : "success"}`}
				show={alertVisible}
				onClose={() => setAlertVisible(false)}
			/>
		</form>
	);
};

export default ContactForm;
